import React, { useEffect, useMemo, useRef, useState } from "react";
import { NavLink } from "react-router-dom";
import { fetchDirectoryUsers } from "../../../api/adminDirectoryApi";

/* --------------------- helpers --------------------- */

function classNames(...xs) {
  return xs.filter(Boolean).join(" ");
}

function safeStr(x) {
  return x == null ? "" : String(x);
}

function cleanStr(x) {
  const s = safeStr(x).trim();
  return s ? s : "";
}

function num(x) {
  const n = Number(x);
  return Number.isFinite(n) ? n : 0;
}

function pct(x, digits = 0) {
  const n = Number(x);
  if (!Number.isFinite(n)) return "—";
  return `${(n * 100).toFixed(digits)}%`;
}

function clamp(n, a, b) {
  return Math.max(a, Math.min(b, n));
}

function dtClean(v) {
  if (!v) return "—";
  try {
    const d = new Date(String(v));
    if (Number.isNaN(d.getTime())) return "—";
    return d.toLocaleString("en-IN", {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return "—";
  }
}

function inr(v, digits = 0) {
  const n = Number(v);
  if (!Number.isFinite(n)) return "—";
  return `₹${n.toLocaleString("en-IN", { maximumFractionDigits: digits })}`;
}

function useDebouncedValue(value, delayMs = 350) {
  const [v, setV] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setV(value), delayMs);
    return () => clearTimeout(t);
  }, [value, delayMs]);
  return v;
}

/* --------------------- UI primitives --------------------- */

function Chip({ tone = "slate", children }) {
  const cls =
    tone === "green"
      ? "bg-emerald-50 text-emerald-800 border-emerald-200"
      : tone === "red"
      ? "bg-rose-50 text-rose-800 border-rose-200"
      : tone === "amber"
      ? "bg-amber-50 text-amber-900 border-amber-200"
      : tone === "blue"
      ? "bg-sky-50 text-sky-900 border-sky-200"
      : "bg-slate-50 text-slate-800 border-slate-200";

  return (
    <span
      className={classNames(
        "inline-flex items-center rounded-md border px-2.5 py-1 text-[11px] font-semibold leading-none whitespace-nowrap",
        cls
      )}
    >
      {children}
    </span>
  );
}

function ProgressMeter({ label, value01, leftLabel, rightLabel, tone = "slate" }) {
  const v = clamp(num(value01), 0, 1);
  const bar =
    tone === "green"
      ? "bg-emerald-500"
      : tone === "red"
      ? "bg-rose-500"
      : tone === "amber"
      ? "bg-amber-500"
      : "bg-slate-700";

  return (
    <div className="rounded-xl border border-slate-200 bg-white px-4 py-3">
      <div className="flex items-center justify-between gap-3">
        <div className="text-[11px] font-semibold uppercase tracking-wider text-slate-500 whitespace-nowrap">
          {label}
        </div>
        <div className="text-sm font-semibold text-slate-800 whitespace-nowrap">
          {pct(v, 0)}
        </div>
      </div>

      <div className="mt-2 h-2 w-full rounded-full bg-slate-100 overflow-hidden">
        <div className={classNames("h-full rounded-full", bar)} style={{ width: `${v * 100}%` }} />
      </div>

      <div className="mt-2 flex items-center justify-between text-[11px] text-slate-500">
        <span className="whitespace-nowrap">{leftLabel}</span>
        <span className="whitespace-nowrap">{rightLabel}</span>
      </div>
    </div>
  );
}

function KpiCard({ title, value, hint, accent = "slate" }) {
  const ring =
    accent === "green"
      ? "ring-emerald-100"
      : accent === "red"
      ? "ring-rose-100"
      : accent === "amber"
      ? "ring-amber-100"
      : accent === "blue"
      ? "ring-sky-100"
      : "ring-slate-100";

  const dot =
    accent === "green"
      ? "bg-emerald-500"
      : accent === "red"
      ? "bg-rose-500"
      : accent === "amber"
      ? "bg-amber-500"
      : accent === "blue"
      ? "bg-sky-500"
      : "bg-slate-700";

  return (
    <div className={classNames("rounded-xl border border-slate-200 bg-white px-4 py-3 ring-4", ring)}>
      <div className="flex items-center justify-between">
        <div className="text-[11px] font-semibold uppercase tracking-wider text-slate-500 whitespace-nowrap">
          {title}
        </div>
        <span className={classNames("h-2 w-2 rounded-full", dot)} aria-hidden="true" />
      </div>

      <div className="mt-1 text-2xl font-semibold text-slate-900 whitespace-nowrap">{value}</div>
      {hint ? (
        <div className="mt-1 text-xs text-slate-500 whitespace-nowrap overflow-hidden text-ellipsis">
          {hint}
        </div>
      ) : null}
    </div>
  );
}

/* ✅ Your exact pagination footer style */
function PaginationFooter({ page, size, totalPages, totalItems, onPrev, onNext, onSize }) {
  const canPrev = page > 0;
  const canNext = page + 1 < totalPages;

  return (
    <div className="px-5 py-3 border-t border-slate-200 bg-white">
      <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
        <div className="text-xs text-slate-500 whitespace-nowrap">
          Showing <span className="font-semibold text-slate-700">{size}</span> • Total{" "}
          <span className="font-semibold text-slate-700">{totalItems}</span>
        </div>

        <div className="flex flex-col sm:flex-row sm:items-center gap-3">
          <div className="flex items-center gap-2">
            <div className="text-xs text-slate-500 whitespace-nowrap">View</div>
            <div className="relative">
              <select
                value={size}
                onChange={(e) => onSize(Number(e.target.value))}
                className="appearance-none h-10 rounded-md border border-slate-200 bg-white pl-3 pr-9 text-sm font-semibold text-slate-700 hover:bg-slate-50"
              >
                {[10, 25, 50, 100, 200].map((n) => (
                  <option key={n} value={n}>
                    {n}
                  </option>
                ))}
              </select>
              <span className="absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none text-slate-500">
                ▼
              </span>
            </div>
          </div>

          <div className="text-sm text-slate-700 whitespace-nowrap">
            Page <span className="font-semibold">{page + 1}</span> of{" "}
            <span className="font-semibold">{Math.max(1, totalPages)}</span>
          </div>

          <div className="flex items-center gap-2">
            <button
              onClick={onPrev}
              disabled={!canPrev}
              className="h-10 px-4 rounded-md border border-slate-200 text-sm font-semibold hover:bg-slate-50 disabled:opacity-50"
            >
              Prev
            </button>
            <button
              onClick={onNext}
              disabled={!canNext}
              className="h-10 px-4 rounded-md border border-slate-200 text-sm font-semibold hover:bg-slate-50 disabled:opacity-50"
            >
              Next
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

/* --------------------- CSV export --------------------- */

function csvEscape(v) {
  const s = v == null ? "" : String(v);
  if (/[",\n]/.test(s)) return `"${s.replaceAll('"', '""')}"`;
  return s;
}

function downloadTextFile(filename, text, mime = "text/csv;charset=utf-8") {
  const blob = new Blob([text], { type: mime });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

/* --------------------- page --------------------- */

export default function DirectoryUsers() {
  const [q, setQ] = useState("");
  const qDebounced = useDebouncedValue(q, 350);

  const [status, setStatus] = useState("ALL");
  const [risk, setRisk] = useState("ALL");
  const [page, setPage] = useState(0);
  const [size, setSize] = useState(25);

  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const [resp, setResp] = useState(null);

  const fetchSeq = useRef(0);

  useEffect(() => {
    setPage(0);
  }, [qDebounced, status, risk, size]);

  useEffect(() => {
    let alive = true;
    const seq = ++fetchSeq.current;

    async function run() {
      setLoading(true);
      setErr("");

      try {
        const data = await fetchDirectoryUsers({
          q: qDebounced,
          status: status === "ALL" ? undefined : status,
          risk: risk === "ALL" ? undefined : risk,
          page,
          size,
        });

        if (!alive || seq !== fetchSeq.current) return;
        setResp(data || null);
      } catch (e) {
        if (!alive || seq !== fetchSeq.current) return;
        setErr(e?.message || "Failed to load directory users");
        setResp(null);
      } finally {
        if (!alive || seq !== fetchSeq.current) return;
        setLoading(false);
      }
    }

    run();
    return () => {
      alive = false;
    };
  }, [qDebounced, status, risk, page, size]);

  const summary = resp?.summary || null;
  const items = resp?.items || [];
  const totalItems = num(resp?.totalItems);
  const totalPages = num(resp?.totalPages);
  const currentPage = num(resp?.page);
  const pageSize = num(resp?.size);

  const exportCsv = () => {
    const stamp = new Date().toISOString().slice(0, 19).replaceAll(":", "-");
    const filename = `users-directory-page-${currentPage + 1}-${stamp}.csv`;

    const header = [
      "ID",
      "Name",
      "Phone",
      "Email",
      "Enabled",
      "HighRisk",
      "Bookings",
      "CompletedBookings",
      "CancelledBookings",
      "Requests",
      "Rides",
      "CancelRate",
      "PendingRefund",
      "TotalBookingValue",
      "TotalWayrootEarnings",
      "LastActivityAt",
      "LatestBookingId",
      "LatestBookingStatus",
    ];

    const lines = [header.map(csvEscape).join(",")];

    items.forEach((u) => {
      const enabled = !!u.enabled;
      const highRisk = !enabled || !!u.highCancelRisk || !!u.hasPendingRefund;

      lines.push(
        [
          u.id,
          u.name,
          u.phone,
          u.email,
          enabled ? "YES" : "NO",
          highRisk ? "YES" : "NO",
          num(u.bookingCount),
          num(u.completedBookingCount),
          num(u.cancelledBookingCount),
          num(u.requestCount),
          num(u.rideCount),
          num(u.cancellationRate),
          u.hasPendingRefund ? num(u.pendingRefundAmountTotal) : 0,
          num(u.totalBookingValue),
          num(u.totalWayrootEarnings),
          u.lastActivityAt || "",
          u.latestBookingId || "",
          u.latestBookingStatus || "",
        ].map(csvEscape).join(",")
      );
    });

    downloadTextFile(filename, lines.join("\n"));
  };

  const topBlock = useMemo(() => {
    if (!summary) return null;

    const totalUsers = num(summary.totalUsers).toLocaleString("en-IN");
    const enabled = num(summary.enabledUsers).toLocaleString("en-IN");
    const blocked = num(summary.blockedUsers).toLocaleString("en-IN");
    const new7d = num(summary.newUsers7d).toLocaleString("en-IN");
    const active30d = num(summary.activeUsers30d).toLocaleString("en-IN");
    const bookings7d = num(summary.bookings7d).toLocaleString("en-IN");
    const refundsUsers = num(summary.usersWithPendingRefund).toLocaleString("en-IN");

    // ✅ NEW all-time money
    const revenueAll = inr(summary.totalRevenueAllTime, 0);
    const earningsAll = inr(summary.wayrootEarningsAllTime, 0);

    const completionRate = num(summary.completionRate7d);
    const cancelRate = num(summary.cancelRate7d);

    const refundShare =
      num(summary.totalUsers) > 0 ? num(summary.usersWithPendingRefund) / num(summary.totalUsers) : 0;

    return (
      <div className="grid grid-cols-12 gap-3">
        {/* KPI strip */}
        <div className="col-span-12 2xl:col-span-8 grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-3">
          <KpiCard title="Total Users" value={totalUsers} hint="All time" accent="blue" />
          <KpiCard title="Enabled" value={enabled} hint="Active accounts" accent="green" />
          <KpiCard title="Blocked" value={blocked} hint="Disabled / blocked" accent="red" />
          <KpiCard title="New (7d)" value={new7d} hint="Signups in last 7 days" accent="blue" />

          <KpiCard title="Active (30d)" value={active30d} hint="Updated recently" accent="green" />
          <KpiCard title="Bookings (7d)" value={bookings7d} hint="Created in last 7 days" accent="blue" />
          <KpiCard title="Total Revenue" value={revenueAll} hint="All time (completed)" accent="blue" />
          <KpiCard title="Wayroot Earnings" value={earningsAll} hint="Fare - payout - GST" accent="amber" />

        </div>

        {/* Meters */}
        <div className="col-span-12 2xl:col-span-4 grid grid-cols-1 gap-3">
          <ProgressMeter
            label="Completion rate (7d)"
            value01={completionRate}
            leftLabel="Lower"
            rightLabel="Higher"
            tone="green"
          />
          <ProgressMeter
            label="Cancel rate (7d)"
            value01={cancelRate}
            leftLabel="Lower"
            rightLabel="Higher"
            tone="red"
          />
         
        </div>
      </div>
    );
  }, [summary]);

  return (
    <div className="min-w-0">
      <div className="rounded-2xl border border-slate-200 bg-white shadow-sm">
        {/* Header */}
        <div className="px-6 py-5 border-b border-slate-200">
          <div className="flex flex-col gap-3 md:flex-row md:items-start md:justify-between">
            <div className="min-w-0">
              <div className="text-xs font-semibold uppercase tracking-wider text-slate-500 whitespace-nowrap">
                Users Directory
              </div>
              <div className="mt-1 text-sm text-slate-600">
                Enterprise view: global user list, risk scan, and quick drill to Support.
              </div>
            </div>

            <div className="flex items-center gap-2">
              <Chip tone="blue">READ ONLY</Chip>
              <Chip>GLOBAL</Chip>
            </div>
          </div>

          {/* KPI + meters */}
          <div className="mt-5">{topBlock}</div>
        </div>

        {/* Filters */}
        <div className="px-6 py-4 border-b border-slate-200 bg-slate-50/40">
          <div className="flex flex-col gap-3 xl:flex-row xl:items-end xl:justify-between">
            <div className="grid gap-3 md:grid-cols-2 xl:flex xl:flex-wrap xl:items-end">
              <div className="w-full xl:min-w-[300px] xl:flex-1">
                <div className="text-xs font-semibold text-slate-600 whitespace-nowrap">Search</div>
                <input
                  value={q}
                  onChange={(e) => setQ(e.target.value)}
                  placeholder="ID / phone / email / name"
                  className="mt-2 h-10 w-full rounded-md border border-slate-200 bg-white px-3 text-sm text-slate-800 placeholder:text-slate-400"
                />
              </div>

              <div>
                <div className="text-xs font-semibold text-slate-600 whitespace-nowrap">Status</div>
                <select
                  value={status}
                  onChange={(e) => setStatus(e.target.value)}
                  className="mt-2 h-10 rounded-md border border-slate-200 bg-white px-3 text-sm text-slate-800 hover:bg-slate-50"
                >
                  <option value="ALL">All</option>
                  <option value="ENABLED">Enabled</option>
                  <option value="BLOCKED">Blocked</option>
                </select>
              </div>

              <div>
                <div className="text-xs font-semibold text-slate-600 whitespace-nowrap">Risk</div>
                <select
                  value={risk}
                  onChange={(e) => setRisk(e.target.value)}
                  className="mt-2 h-10 rounded-md border border-slate-200 bg-white px-3 text-sm text-slate-800 hover:bg-slate-50"
                >
                  <option value="ALL">All</option>
                  <option value="HIGH">High only</option>
                </select>
              </div>
            </div>

            {/* ✅ right actions */}
            <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between xl:justify-end">
              <div className="text-xs text-slate-500 whitespace-nowrap">
                Showing: <span className="text-slate-800">{loading ? "…" : items.length}</span> users
              </div>

              <button
                onClick={exportCsv}
                disabled={loading || items.length === 0}
                className="inline-flex h-10 items-center justify-center rounded-md border border-slate-200 bg-white px-4 text-sm font-semibold text-slate-900 hover:bg-slate-50 disabled:opacity-50"
              >
                Export CSV
              </button>
            </div>
          </div>
        </div>

        {/* Table */}
        <div className="overflow-x-auto">
          <table className="min-w-[1420px] w-full">
            <thead className="bg-white sticky top-0 z-10">
              <tr className="text-left text-[11px] font-semibold uppercase tracking-wider text-slate-500 whitespace-nowrap">
                <th className="px-6 py-3 w-[90px]">ID</th>
                <th className="px-6 py-3 w-[240px]">Name</th>
                <th className="px-6 py-3 w-[180px]">Phone</th>
               
                <th className="px-6 py-3 w-[170px]">Status</th>
                <th className="px-6 py-3 w-[110px] text-right">Bookings</th>
                <th className="px-6 py-3 w-[110px] text-right">Requests</th>
                <th className="px-6 py-3 w-[110px] text-right">Rides</th>
                <th className="px-6 py-3 w-[110px] text-right">Cancel%</th>
                <th className="px-6 py-3 w-[170px]">Refund</th>

                {/* ✅ NEW money columns */}
                <th className="px-6 py-3 w-[170px] text-right">Booking Value</th>
                <th className="px-6 py-3 w-[190px] text-right">Wayroot Earnings</th>

                <th className="px-6 py-3 w-[200px]">Last activity</th>
                <th className="px-6 py-3 w-[170px]">Latest booking</th>
                <th className="px-6 py-3 w-[140px]">Action</th>
              </tr>
            </thead>

            <tbody className="divide-y divide-slate-200">
              {loading ? (
                Array.from({ length: 8 }).map((_, i) => (
                  <tr key={i} className="animate-pulse">
                    {Array.from({ length: 15 }).map((__, j) => (
                      <td key={j} className="px-6 py-4">
                        <div className="h-4 w-full rounded bg-slate-100" />
                      </td>
                    ))}
                  </tr>
                ))
              ) : err ? (
                <tr>
                  <td colSpan={15} className="px-6 py-8">
                    <div className="rounded-xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-800">
                      {err}
                    </div>
                  </td>
                </tr>
              ) : items.length === 0 ? (
                <tr>
                  <td colSpan={15} className="px-6 py-10 text-sm text-slate-600">
                    No users found.
                  </td>
                </tr>
              ) : (
                items.map((u) => {
                  const enabled = !!u.enabled;
                  const highRisk = !enabled || !!u.highCancelRisk || !!u.hasPendingRefund;

                  const cancelRate = pct(u.cancellationRate, 0);
                  const latestBooking = u.latestBookingId ? `#${safeStr(u.latestBookingId)}` : "—";

                  const refundText = u.hasPendingRefund
                    ? `₹${num(u.pendingRefundAmountTotal).toLocaleString("en-IN", { maximumFractionDigits: 0 })}`
                    : "—";

                  return (
                    <tr key={u.id} className="hover:bg-slate-50">
                      <td className="px-6 py-4 text-sm text-slate-900 whitespace-nowrap">{u.id}</td>

                      <td className="px-6 py-4 text-sm text-slate-900 whitespace-nowrap overflow-hidden text-ellipsis max-w-[240px]">
                        {u.name || "—"}
                      </td>

                      <td className="px-6 py-4 text-sm text-slate-800 whitespace-nowrap overflow-hidden text-ellipsis max-w-[180px]">
                        {u.phone || "—"}
                      </td>

                     

                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center gap-2">
                          {enabled ? <Chip tone="green">ENABLED</Chip> : <Chip tone="red">BLOCKED</Chip>}
                          {highRisk ? <Chip tone="amber">HIGH</Chip> : <Chip>OK</Chip>}
                        </div>
                      </td>

                      <td className="px-6 py-4 text-sm text-slate-800 text-right whitespace-nowrap">
                        {num(u.bookingCount).toLocaleString("en-IN")}
                      </td>

                      <td className="px-6 py-4 text-sm text-slate-800 text-right whitespace-nowrap">
                        {num(u.requestCount).toLocaleString("en-IN")}
                      </td>

                      <td className="px-6 py-4 text-sm text-slate-800 text-right whitespace-nowrap">
                        {num(u.rideCount).toLocaleString("en-IN")}
                      </td>

                      <td className="px-6 py-4 text-sm text-slate-800 text-right whitespace-nowrap">
                        {cancelRate}
                      </td>

                      <td className="px-6 py-4 text-sm text-slate-800 whitespace-nowrap">{refundText}</td>

                      {/* ✅ NEW: money cells */}
                      <td className="px-6 py-4 text-sm text-slate-800 text-right whitespace-nowrap">
                        {inr(u.totalBookingValue, 0)}
                      </td>

                      <td className="px-6 py-4 text-sm text-slate-800 text-right whitespace-nowrap">
                        {inr(u.totalWayrootEarnings, 0)}
                      </td>

                      <td className="px-6 py-4 text-sm text-slate-700 whitespace-nowrap">
                        {dtClean(u.lastActivityAt)}
                      </td>

                      <td className="px-6 py-4 text-sm text-slate-700 whitespace-nowrap overflow-hidden text-ellipsis max-w-[170px]">
                        {latestBooking}
                      </td>

                      <td className="px-6 py-4 whitespace-nowrap">
                        <NavLink
                          to={`/admin/support/users/${encodeURIComponent(String(u.id))}`}
                          className="inline-flex h-9 items-center justify-center rounded-md border border-slate-200 bg-white px-3 text-sm font-semibold text-slate-900 hover:bg-slate-50"
                        >
                          Open
                        </NavLink>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        <PaginationFooter
          page={currentPage}
          size={pageSize}
          totalPages={totalPages}
          totalItems={totalItems}
          onPrev={() => setPage((p) => Math.max(0, p - 1))}
          onNext={() => setPage((p) => (p + 1 < totalPages ? p + 1 : p))}
          onSize={(n) => setSize(clamp(n, 10, 200))}
        />
      </div>
    </div>
  );
}
